/**
 * PanelErrorBoundary (TMMPDM-0016) — keeps a render error in one tenant tab from reaching the
 * router `errorElement` and replacing the whole app. Shows an error Alert with Try again, which
 * clears the boundary so the child remounts. React 19 still needs a class for this.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Alert, Button } from '@mui/material';
import { useCopy } from '@/hooks/useCopy';

interface PanelErrorBoundaryProps {
  children: ReactNode;
}

interface PanelErrorBoundaryState {
  failed: boolean;
}

export class PanelErrorBoundary extends Component<
  PanelErrorBoundaryProps,
  PanelErrorBoundaryState
> {
  override state: PanelErrorBoundaryState = { failed: false };

  static getDerivedStateFromError(): PanelErrorBoundaryState {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Same posture as RouteError: log in development only.
    if (import.meta.env.DEV) console.error('Panel error:', error, info.componentStack);
  }

  override render() {
    if (this.state.failed) {
      return <PanelErrorFallback onRetry={() => this.setState({ failed: false })} />;
    }
    return this.props.children;
  }
}

function PanelErrorFallback({ onRetry }: { onRetry: () => void }) {
  const { t } = useCopy();
  return (
    <Alert
      severity="error"
      action={
        <Button color="inherit" size="small" onClick={onRetry}>
          {t('common.tryAgain', 'Try again')}
        </Button>
      }
    >
      {t('common.panelError', "This section couldn't be shown.")}
    </Alert>
  );
}
